import { useCallback, useMemo } from "react";

import useAccountHoldingsV2 from "@fartherfinance/frontend/api/Accounts/hooks/useAccountHoldingsV2";
import useGetFartherManagedAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useGetFartherManagedAccounts";
import {
  ExtendedFartherHoldingsArraySchema,
  SecuritySchema,
} from "@fartherfinance/frontend/api/DataLake/Types";
import useGetExcludedAssets from "@fartherfinance/frontend/api/Investment/hooks/useGetExcludedAssets";
import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import useGetTlhSwaps from "@fartherfinance/frontend/api/Investment/hooks/useGetTlhSwaps";
import useGetPortfolioPlanAssignments from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanAssignment";
import {
  ClientId,
  HookResult,
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
  RequestConfig,
  SecurityId,
  Ticker,
} from "@fartherfinance/frontend/api/Types";
import {
  SWEEP_ACCOUNT_SECURITY_SYMBOLS,
  SweepAccountSecuritySymbol,
} from "@fartherfinance/frontend/constants/Securities";

import { filterHoldings } from "../helpers";

export type HoldingWithExclusionStatus = {
  securityId: SecurityId;
  ticker?: Ticker | null;
  assetName?: string | null;
  marketValue?: number | null;
  quantity?: number | null;
  isInPortfolio: boolean;
  isExcluded: boolean;
  description?: string | null;
  account: string | null;
  isTLHSecurity: boolean;
  isSweepAccountSecurity: boolean;
  securityDetails?: SecuritySchema;
  holding: ExtendedFartherHoldingsArraySchema["records"][number];
};

interface PortfolioHoldingsParams {
  clientId: ClientId;
  portfolioPlanId: PortfolioPlanId;
  portfolioPlanAssignmentId: PortfolioPlanAssignmentId;
}

type UsePortfolioHoldingsWithExcludedSecuritiesResult = HookResult<
  HoldingWithExclusionStatus[]
>;

/**
 * Custom hook that combines account holdings with portfolio plan securities and excluded securities
 * to create a merged list that shows if a security is in the portfolio or excluded.
 *
 * @param params Object containing clientId, portfolioPlanId, and portfolioPlanAssignmentId
 * @param queryParams Optional query parameters for filtering account holdings
 * @param config Request configuration with JWT for authentication
 * @returns Object containing the merged holdings list, loading status, and any errors
 */
const usePortfolioHoldingsWithExcludedSecurities = (
  params: PortfolioHoldingsParams,
  config: RequestConfig | null
): UsePortfolioHoldingsWithExcludedSecuritiesResult => {
  const { clientId, portfolioPlanId, portfolioPlanAssignmentId } = params;

  const managedAccounts = useGetFartherManagedAccounts(clientId, config);
  const givenAVirtualAccountId = useCallback(
    (virtualAccountId: string | null | undefined): string => {
      if (managedAccounts.data) {
        const account = managedAccounts.data.find(
          (account) => account.virtualAccountId === virtualAccountId
        );
        // If the account is not found, return null
        if (!account) {
          return "Unknown Account";
        }

        return `${account.accountDetails.displayName} (${
          account.accountDetails.custodianAccountNumber
            ? account.accountDetails.custodianAccountNumber
            : "N/A"
        })`;
      }

      return "Unknown Account";
    },
    [managedAccounts.data]
  );

  const assignment = useGetPortfolioPlanAssignments(
    {
      abstractPortfolioId: portfolioPlanId,
      portfolioPlanAssignmentId,
    },
    config
  );

  const accounts = assignment.data?.accounts;

  const {
    data: accountHoldings,
    isLoading: isLoadingHoldings,
    hasError: hasHoldingsError,
  } = useAccountHoldingsV2(
    clientId,
    accounts
      ? {
          virtualAccountIds: accounts,
          $expand: ["securityDetails"],
        }
      : null,
    config
  );

  // Get portfolio plan to extract securities
  const {
    data: portfolioPlan,
    isLoading: isLoadingPortfolio,
    hasError: hasPortfolioError,
  } = useGetPortfolioPlanById(portfolioPlanId, {}, config);

  // Get excluded securities
  const {
    data: excludedSecurities,
    isLoading: isLoadingExcluded,
    hasError: hasExcludedError,
  } = useGetExcludedAssets(portfolioPlanId, portfolioPlanAssignmentId, config);

  // Get TLH ongoing swaps
  const {
    data: tlhSwaps,
    isLoading: isLoadingTlhSwaps,
    hasError: hasTlhSwapsError,
  } = useGetTlhSwaps(
    {
      clientId,
    },
    config
  );

  // Extract portfolio securities from the plan
  const portfolioSecurities: SecurityId[] = useMemo(() => {
    if (portfolioPlan?.data?.plan?.entries === undefined) {
      return [];
    }

    return portfolioPlan.data.plan.entries
      .filter((entry) => entry.type === "Security")
      .map((entry) => entry.securityId);
  }, [portfolioPlan]);

  // Extract excluded security IDs
  const excludedSecurityIds = useMemo(() => {
    if (!excludedSecurities) {
      return [];
    }
    return excludedSecurities.map((security) => security.securityId);
  }, [excludedSecurities]);

  // Extract TLH security IDs from the TLH swaps data
  const tlhSecurityIds = useMemo(() => {
    if (!tlhSwaps || !tlhSwaps.length) {
      return [];
    }

    return tlhSwaps.map((swap) => swap.replacementSecurity);
  }, [tlhSwaps]);

  // Combine all data to create the merged holdings list
  const holdingsWithExclusionStatus = useMemo(() => {
    if (accountHoldings == null || accountHoldings.records == null) {
      return [];
    }

    // First, apply our standardized filtering to get non-cash, unique holdings
    const filteredHoldings = filterHoldings(accountHoldings.records);

    // Then map the filtered holdings to our desired format
    return filteredHoldings.map((holding) => {
      const securityId = holding.farther_security_id as SecurityId;

      const ticker =
        holding.ticker !== null ? Ticker.parse(holding.ticker) : undefined;

      // Check if this security is a sweep account/money market fund
      const isSweepAccount = ticker
        ? SWEEP_ACCOUNT_SECURITY_SYMBOLS.has(
            ticker.toUpperCase() as SweepAccountSecuritySymbol
          )
        : false;

      const result: HoldingWithExclusionStatus = {
        securityId,
        ticker,
        assetName: holding.asset_name,
        marketValue: holding.market_value,
        quantity: holding.quantity,
        isInPortfolio: portfolioSecurities.includes(securityId),
        isExcluded: excludedSecurityIds.includes(securityId),
        isTLHSecurity: tlhSecurityIds.includes(securityId),
        isSweepAccountSecurity: isSweepAccount,
        description:
          holding.securityDetails?.description ??
          holding.securityDetails?.name ??
          holding.asset_name,
        account: givenAVirtualAccountId(holding.farther_virtual_account_id),
        securityDetails: holding.securityDetails,
        holding,
      };

      return result;
    });
  }, [
    accountHoldings,
    portfolioSecurities,
    excludedSecurityIds,
    tlhSecurityIds,
    givenAVirtualAccountId,
  ]);

  return useMemo(() => {
    // Check if we're loading any data
    const isLoading =
      isLoadingHoldings ||
      isLoadingPortfolio ||
      isLoadingExcluded ||
      isLoadingTlhSwaps;

    // Determine if there's an error
    const error =
      hasHoldingsError ||
      hasPortfolioError ||
      hasExcludedError ||
      hasTlhSwapsError
        ? new Error(
            "Error fetching usePortfolioHoldingsWithExcludedSecurities data"
          )
        : null;

    if (error) {
      return {
        data: undefined,
        isLoading: false,
        hasError: true,
        error: error,
      };
    }

    if (isLoading) {
      return {
        data: undefined,
        isLoading: true,
        hasError: false,
      };
    }

    return {
      data: holdingsWithExclusionStatus,
      isLoading: false,
      hasError: false,
    };
  }, [
    hasExcludedError,
    hasHoldingsError,
    hasPortfolioError,
    hasTlhSwapsError,
    holdingsWithExclusionStatus,
    isLoadingExcluded,
    isLoadingHoldings,
    isLoadingPortfolio,
    isLoadingTlhSwaps,
  ]);
};

export default usePortfolioHoldingsWithExcludedSecurities;
